<script lang="ts">
  import {
    ACHIEVEMENTS, CONSTELLATION_NODES, DOCTRINES, DOCTRINE_UNLOCK_ASCENSIONS, constellationNodeLockReasons, harmonicBondMultiplier, nextEchoShardTarget, ownsConstellationNode, pendingEchoShards, prestigeUpgradeAscensionRequirement,
    prestigeUpgradeCost, prestigeUpgradeMaxRank, prestigeUpgradePrerequisite,
    productionValueForEchoShards,
    type ConstellationNodeDefinition, type ConstellationNodeId, type DoctrineActionResult, type DoctrineId, type GameState, type PrestigeActionResult, type PrestigeUpgradeId, type SkillId, SKILLS
  } from '@rsvjelle/game-core';
  import { formatNumber } from '../lib/display.ts';

  interface Props {
    gameState: GameState;
    onAscend: () => PrestigeActionResult;
    onBuyUpgrade: (upgrade: PrestigeUpgradeId) => PrestigeActionResult;
    onBuyConstellation: (node: ConstellationNodeId) => PrestigeActionResult;
    onChooseDoctrine: (doctrine: DoctrineId, focusedSkill?: SkillId) => DoctrineActionResult;
  }
  let { gameState, onAscend, onBuyUpgrade, onBuyConstellation, onChooseDoctrine }: Props = $props();
  let pending = $derived(pendingEchoShards(gameState));
  let currentShardThreshold = $derived(productionValueForEchoShards(pending));
  let nextShardTarget = $derived(nextEchoShardTarget(gameState));
  let progress = $derived(Math.min(100, Math.max(0, (gameState.run.productionValue - currentShardThreshold) / (nextShardTarget - currentShardThreshold) * 100)));
  let confirming = $state(false);
  let doctrineError = $state('');
  let pendingDoctrine = $state<DoctrineId | null>(null);
  let focusedSkill = $state<SkillId>('Woodcutting');
  let activeDoctrine = $derived(DOCTRINES.find((doctrine) => doctrine.id === gameState.run.doctrine?.id));
  $effect(() => { if (!pendingDoctrine && !gameState.run.doctrine) focusedSkill = gameState.run.activeSkill; });
  const upgrades: Array<{ id: PrestigeUpgradeId; route: 'forge' | 'knowledge' | 'time' | 'prosperity'; icon: string; name: string; description: string }> = [
    { id: 'eternalCraft', route: 'forge', icon: '⚒️', name: 'Eternal Craft', description: '+12% global production per rank' },
    { id: 'harmonicBond', route: 'forge', icon: '🔗', name: 'Harmonic Bond', description: 'Earned skill levels add global XP and production' },
    { id: 'echoingKnowledge', route: 'knowledge', icon: '📜', name: 'Echoing Knowledge', description: '+10% global XP per rank' },
    { id: 'beyondTheVeil', route: 'knowledge', icon: '🌌', name: 'Beyond the Veil', description: 'Doubles XP at level 120+ per rank' },
    { id: 'deepSlumber', route: 'time', icon: '🌙', name: 'Deep Slumber', description: '+2 hours offline cap per rank' },
    { id: 'perfectRecall', route: 'time', icon: '🧠', name: 'Perfect Recall', description: '+5% offline efficiency per rank' },
    { id: 'twinEchoes', route: 'time', icon: '👥', name: 'Twin Echoes', description: 'Two chosen skills gather resources without XP' },
    { id: 'resonantExchange', route: 'prosperity', icon: '⚖️', name: 'Resonant Exchange', description: '+5% sale value per rank' },
    { id: 'legacyVault', route: 'prosperity', icon: '🗝️', name: 'Legacy Vault', description: 'Begin each Ascension with permanent starting gold' },
    { id: 'swiftHands', route: 'prosperity', icon: '⚡', name: 'Swift Hands', description: '+25% manual action power per rank' }
  ];
  const routes = [
    { id: 'forge' as const, icon: '⚒️', name: 'Forge', subtitle: 'Production and Halls' },
    { id: 'knowledge' as const, icon: '📜', name: 'Knowledge', subtitle: 'XP and the level-120 frontier' },
    { id: 'time' as const, icon: '🌙', name: 'Time', subtitle: 'Offline and passive progress' },
    { id: 'prosperity' as const, icon: '⚖️', name: 'Prosperity', subtitle: 'Gold and active moments' }
  ];
  let nextKeystone = $derived(CONSTELLATION_NODES.filter((node) => !ownsConstellationNode(gameState, node.id)).sort((left, right) => {
    const availability = constellationNodeLockReasons(gameState, left.id).length - constellationNodeLockReasons(gameState, right.id).length;
    return availability || left.requiredAscensions - right.requiredAscensions;
  })[0]);

  function ascend() {
    onAscend();
    confirming = false;
  }
  function buy(id: PrestigeUpgradeId) {
    onBuyUpgrade(id);
  }
  function buyNode(id: ConstellationNodeId) {
    onBuyConstellation(id);
  }
  function achievementName(id?: string): string {
    return ACHIEVEMENTS.find((achievement) => achievement.id === id)?.name ?? 'Achievement';
  }
  function nodeLockLabels(node: ConstellationNodeDefinition): string[] {
    return constellationNodeLockReasons(gameState, node.id).map((reason) => {
      if (reason === 'Achievement') return achievementName(node.requiredAchievement);
      for (const upgrade of upgrades) if (reason.startsWith(upgrade.id)) return reason.replace(upgrade.id, upgrade.name);
      return reason;
    });
  }
  function swearDoctrine(): void {
    if (!pendingDoctrine) return;
    const result = onChooseDoctrine(pendingDoctrine, pendingDoctrine === 'specialist' ? focusedSkill : undefined);
    if (result.ok) {
      pendingDoctrine = null;
      doctrineError = '';
    } else doctrineError = result.reason ?? 'Doctrine choice failed.';
  }
</script>

<section class="view-stack" aria-labelledby="ascension-title">
  <div class="section-heading"><div><p class="eyebrow">BEYOND THE VEIL</p><h2 id="ascension-title">Realm Ascension</h2></div><span class="shard-balance">A{gameState.prestige.ascensions} · ✦ {formatNumber(gameState.prestige.echoShards, 0)}</span></div>
  <article class="ascension-hero">
    <div class="shard-orbit"><span>✦</span></div>
    <p>Every resource produced this run counts—even after spending or selling</p><h3>{pending > 0 ? `${pending} Echo Shard${pending === 1 ? '' : 's'} await` : 'The echoes are still faint'}</h3>
    <div class="ascension-progress"><span style={`width:${progress}%`}></span></div>
    <small>{formatNumber(gameState.run.productionValue, 0)} run value · next shard at {formatNumber(nextShardTarget, 0)}</small>
    {#if confirming}
      <div class="ascension-confirm"><strong>Reset this run?</strong><span>Resources, gold, tools and buildings return to zero. Permanent upgrades and statistics remain.</span><div><button class="cancel" type="button" onclick={() => { confirming = false; }}>Cancel</button><button type="button" onclick={ascend}>Confirm Ascension</button></div></div>
    {:else}
      <button type="button" disabled={pending === 0} onclick={() => { confirming = true; }}>Ascend this realm</button>
    {/if}
  </article>
  <article class="ascension-unlocks">
    <div><p class="eyebrow">NEXT MAJOR STAR</p><h3>{nextKeystone ? nextKeystone.name : 'The Constellation is complete'}</h3></div>
    {#if nextKeystone}
      <div class="next-star"><span>{nextKeystone.icon}</span><strong>✦ {nextKeystone.cost} · A{nextKeystone.requiredAscensions}</strong><small>{nextKeystone.description}</small><em>{nodeLockLabels(nextKeystone).length ? `Still needed: ${nodeLockLabels(nextKeystone).join(' · ')}` : 'Ready to awaken'}</em></div>
    {:else}
      <div class="next-star"><span>👑</span><strong>Every known star awakened</strong><small>Your permanent build is complete.</small></div>
    {/if}
  </article>
  <article class="doctrine-chamber">
    <div class="doctrine-heading"><div><p class="eyebrow">ONE BUILD · ONE RUN</p><h3>Ascension Doctrines</h3></div><span>Unlocks at A{DOCTRINE_UNLOCK_ASCENSIONS}</span></div>
    {#if gameState.prestige.ascensions < DOCTRINE_UNLOCK_ASCENSIONS}
      <div class="doctrine-locked"><span>🜂</span><div><strong>The chamber is still sealed</strong><small>Complete {DOCTRINE_UNLOCK_ASCENSIONS - gameState.prestige.ascensions} more Ascension{DOCTRINE_UNLOCK_ASCENSIONS - gameState.prestige.ascensions === 1 ? '' : 's'} to choose a build for each run.</small></div></div>
    {:else if activeDoctrine}
      <div class="doctrine-active"><span>{activeDoctrine.icon}</span><div><small>ACTIVE UNTIL NEXT ASCENSION</small><h4>{activeDoctrine.name}{#if gameState.run.doctrine?.focusedSkill} · {gameState.run.doctrine.focusedSkill}{/if}</h4><p>{activeDoctrine.description}</p><b>{activeDoctrine.advantage}</b><em>{activeDoctrine.drawback}</em></div></div>
    {:else}
      <p class="doctrine-intro">Choose once for this run. The Doctrine disappears at Ascension, allowing a completely different build next time.</p>
      <div class="doctrine-grid">
        {#each DOCTRINES as doctrine}
          <article class:selected={pendingDoctrine === doctrine.id}>
            <span>{doctrine.icon}</span><div><h4>{doctrine.name}</h4><p>{doctrine.description}</p><b>{doctrine.advantage}</b><em>{doctrine.drawback}</em></div>
            <button type="button" onclick={() => { pendingDoctrine = doctrine.id; doctrineError = ''; }}>Choose</button>
          </article>
        {/each}
      </div>
    {/if}
  </article>
  <article class="constellation-shell">
    <div class="constellation-heading"><div><p class="eyebrow">PERMANENT ACCOUNT BUILD</p><h3>Ascension Constellation</h3></div><small>Follow any route · eventually master them all</small></div>
    <div class="constellation-routes">
      {#each routes as route}
        {@const routeKeystone = CONSTELLATION_NODES.find((node) => node.route === route.id)!}
        <section class={`constellation-route ${route.id}`}>
          <header><span>{route.icon}</span><div><h4>{route.name}</h4><small>{route.subtitle}</small></div></header>
          <div class="route-track">
            {#each upgrades.filter((upgrade) => upgrade.route === route.id) as upgrade}
              {@const rank = gameState.prestige.upgrades[upgrade.id] ?? 0}
              {@const maxRank = prestigeUpgradeMaxRank(upgrade.id)}
              {@const cost = prestigeUpgradeCost(gameState, upgrade.id)}
              {@const ascensionRequirement = prestigeUpgradeAscensionRequirement(gameState, upgrade.id)}
              {@const prerequisite = prestigeUpgradePrerequisite(gameState, upgrade.id)}
              <article class:active={rank > 0} class:locked={Boolean(prerequisite) || gameState.prestige.ascensions < ascensionRequirement} class="constellation-upgrade route-node">
                <span>{upgrade.icon}</span>
                <div><h5>{upgrade.name}</h5><p>{upgrade.description}</p>{#if upgrade.id === 'harmonicBond'}<small>Current: +{formatNumber((harmonicBondMultiplier(gameState) - 1) * 100, 2)}%</small>{/if}{#if prerequisite}<small>Requires {upgrades.find((item) => item.id === prerequisite.id)?.name ?? prerequisite.id} rank {prerequisite.rank}</small>{:else if gameState.prestige.ascensions < ascensionRequirement}<small>Requires A{ascensionRequirement}</small>{/if}</div>
                <div class="prestige-buy"><strong>{rank}/{maxRank}</strong><button type="button" disabled={rank >= maxRank || gameState.prestige.echoShards < cost || gameState.prestige.ascensions < ascensionRequirement || Boolean(prerequisite)} onclick={() => buy(upgrade.id)}>{rank >= maxRank ? 'MAX' : `✦ ${cost}`}</button></div>
              </article>
            {/each}
            {#if routeKeystone}
              {@const owned = ownsConstellationNode(gameState, routeKeystone.id)}
              {@const locks = constellationNodeLockReasons(gameState, routeKeystone.id)}
              <article class:active={owned} class:locked={!owned && locks.length > 0} class="constellation-keystone route-node">
                <span>{routeKeystone.icon}</span><div><small>ROUTE KEYSTONE</small><h5>{routeKeystone.name}</h5><p>{routeKeystone.description}</p>{#if !owned && locks.length}<em>Requires {nodeLockLabels(routeKeystone).join(' · ')}</em>{/if}</div>
                <button type="button" disabled={owned || locks.length > 0 || gameState.prestige.echoShards < routeKeystone.cost} onclick={() => buyNode(routeKeystone.id)}>{owned ? 'AWAKENED' : `✦ ${routeKeystone.cost}`}</button>
              </article>
            {/if}
          </div>
        </section>
      {/each}
    </div>
    <section class="convergence-field">
      <div><p class="eyebrow">CROSS-ROUTE CONSTELLATION</p><h4>Convergence</h4><small>These stars demand mastery across multiple paths.</small></div>
      <div class="convergence-nodes">
        {#each CONSTELLATION_NODES.filter((node) => node.route === 'convergence') as node}
          {@const owned = ownsConstellationNode(gameState, node.id)}
          {@const locks = constellationNodeLockReasons(gameState, node.id)}
          <article class:active={owned} class:locked={!owned && locks.length > 0} class:grand={node.id === 'grandConvergence'}>
            <span>{node.icon}</span><div><small>{node.id === 'grandConvergence' ? 'FINAL STAR' : 'CONVERGENCE STAR'}</small><h5>{node.name}</h5><p>{node.description}</p>{#if !owned && locks.length}<em>Requires {nodeLockLabels(node).join(' · ')}</em>{/if}</div>
            <button type="button" disabled={owned || locks.length > 0 || gameState.prestige.echoShards < node.cost} onclick={() => buyNode(node.id)}>{owned ? 'AWAKENED' : `✦ ${node.cost}`}</button>
          </article>
        {/each}
      </div>
    </section>
  </article>
</section>

{#if pendingDoctrine}
  {@const choice = DOCTRINES.find((doctrine) => doctrine.id === pendingDoctrine)!}
  <div class="modal-backdrop">
    <dialog open class="doctrine-modal" aria-labelledby="doctrine-confirm-title">
      <span class="doctrine-modal-icon">{choice.icon}</span>
      <div><p class="eyebrow">ONE VOW · THIS RUN</p><h3 id="doctrine-confirm-title">Swear {choice.name}?</h3></div>
      <p>{choice.description}</p>
      <div class="doctrine-modal-effects"><b>{choice.advantage}</b><em>{choice.drawback}</em></div>
      {#if choice.needsFocusedSkill}
        <label>Focused skill<select bind:value={focusedSkill}>{#each SKILLS as skill}<option value={skill}>{skill}</option>{/each}</select></label>
      {/if}
      <small>This choice is locked until your next Ascension.</small>
      {#if doctrineError}<p class="doctrine-modal-error" role="alert">{doctrineError}</p>{/if}
      <div class="doctrine-modal-actions"><button class="cancel" type="button" onclick={() => { pendingDoctrine = null; doctrineError = ''; }}>Cancel</button><button type="button" onclick={swearDoctrine}>Swear Doctrine</button></div>
    </dialog>
  </div>
{/if}
